"use client";

import * as React from "react";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import { type VariantProps, cva } from "class-variance-authority";

import { PortalWrapper } from "../prim.portal-wrapper";
import { cn } from "../util.cn";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

interface SheetPortalProps
  extends React.ComponentProps<typeof SheetPrimitive.Portal> {}
const SheetPortal = ({ children, ...props }: SheetPortalProps) => {
  return (
    <SheetPrimitive.Portal {...props}>
      <PortalWrapper>{children}</PortalWrapper>
    </SheetPrimitive.Portal>
  );
};

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <SheetPrimitive.Overlay
      className={cn(
        `
          fixed inset-0 z-50 bg-overlay
          data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0
          data-[state=open]:animate-in
          data-[state=open]:fade-in-0
        `,
        className,
      )}
      {...props}
      ref={ref}
    />
  );
});
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  `
    fixed z-50 gap-4 bg-panel p-6 shadow-4 transition
    ease-in-out
    data-[state=closed]:duration-100
    data-[state=closed]:animate-out
    data-[state=open]:duration-200
    data-[state=open]:animate-in
  `,
  {
    defaultVariants: {
      disablePadding: false,
      side: "right",
    },
    variants: {
      disablePadding: {
        true: `p-0`,
      },
      side: {
        bottom: `
          inset-x-0 bottom-0 border-t
          data-[state=closed]:slide-out-to-bottom
          data-[state=open]:slide-in-from-bottom
        `,
        left: `
          inset-y-0 left-0 h-full w-3/4 max-w-screen-xs
          border-r
          data-[state=closed]:slide-out-to-left
          data-[state=open]:slide-in-from-left
        `,
        right: `
          inset-y-0 right-0 h-full w-3/4 max-w-screen-xs
          border-l
          data-[state=closed]:slide-out-to-right
          data-[state=open]:slide-in-from-right
        `,
        top: `
          inset-x-0 top-0 border-b
          data-[state=closed]:slide-out-to-top
          data-[state=open]:slide-in-from-top
        `,
      },
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> {
  disablePortalWrapper?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ children, className, disablePadding, side = "right", disablePortalWrapper = false, ...props }, ref) => {
  if (disablePortalWrapper) {
    return (
      <>
        <SheetOverlay />
        <SheetPrimitive.Content
          className={cn(sheetVariants({ disablePadding, side }), className)}
          ref={ref}
          {...props}
        >
          {children}
        </SheetPrimitive.Content>
      </>
    );
  }

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        className={cn(sheetVariants({ disablePadding, side }), className)}
        ref={ref}
        {...props}
      >
        {children}
        {/* <SheetPrimitive.Close className={`
          absolute right-4 top-4 rounded-3 opacity-70
          ring-offset-background transition-opacity
          disabled:pointer-events-none
          focus:outline-none focus:ring-2
          focus:ring-offset-2
          hover:opacity-100
        `}
        >
          <Cross2Icon className="size-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close> */}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        `
          flex flex-col space-y-2 text-center
          sm:text-left
        `,
        className,
      )}
      {...props}
    />
  );
};
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        `
          flex flex-col-reverse
          sm:flex-row sm:justify-end sm:space-x-2
        `,
        className,
      )}
      {...props}
    />
  );
};
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => {
  return (
    <SheetPrimitive.Title
      className={cn("text-3 font-semibold", className)}
      ref={ref}
      {...props}
    />
  );
});
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => {
  return (
    <SheetPrimitive.Description
      className={cn("text-2", className)}
      ref={ref}
      {...props}
    />
  );
});
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet as Root,
  SheetClose as Close,
  SheetContent as Content,
  SheetDescription as Description,
  SheetFooter as Footer,
  SheetHeader as Header,
  SheetOverlay as Overlay,
  SheetPortal as Portal,
  SheetTitle as Title,
  SheetTrigger as Trigger,
};
